import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1464219789935-c2d9d9eefd39?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-top: 3rem;
`;

const ServiceCard = styled.div`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.8rem;
  color: #004744;
  margin-bottom: 1rem;
  font-style: italic;
  text-align: center;
`;

const CTAButton = styled.a`
  display: inline-block;
  background-color: transparent;
  color: #004744;
  border: 1px solid #004744;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  text-transform: uppercase;
  text-decoration: none;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    background-color: rgba(0, 71, 68, 0.1);
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 71, 68, 0.2);
  }
`;

const CTASection = styled.div`
  margin-top: 4rem;
  text-align: center;
`;

const Buses: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  // Bus service providers
  const busServices = [
    {
      name: 'Check My Bus',
      url: 'https://www.checkmybus.it/'
    },
    {
      name: 'Trainline - Bus',
      url: 'https://www.thetrainline.com/it/autobus'
    },
    {
      name: 'Bus Radar',
      url: 'https://busradar.it/'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Autobus | J-O-H' : 'Buses | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri i migliori servizi di autobus per i tuoi spostamenti in Italia' 
            : 'Discover the best bus services for your travels in Italy'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/transportation">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'AUTOBUS' : 'BUSES'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Scegli comfort e comodità. Scegli la tua soluzione di trasporto autobus ideale. Pianifica oggi il tuo prossimo viaggio e lascia che la tua avventura cominci con il piede giusto.'
              : 'Choose comfort and convenience. Choose your ideal bus transport solution. Plan your next trip today and let your adventure get started on the right foot.'}
          </Description>
          
          <ServiceGrid>
            {busServices.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceTitle>{service.name}</ServiceTitle>
                <CTAButton href={service.url} target="_blank" rel="noopener noreferrer">
                  {language === 'it' ? 'Visita il sito' : 'Visit website'}
                </CTAButton>
              </ServiceCard>
            ))}
          </ServiceGrid>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Buses; 